<script setup>
  import { Pencil } from "lucide-vue-next";
  import { storeToRefs } from "pinia";

  import ListContainer from "@/components/episto-ui/list/ListContainer.vue";
  import ListItem from "@/components/episto-ui/list/ListItem.vue";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";

  import { useSqlQueryStore } from "@stores/sqlQuery";

  defineProps({
    templates: { type: Array, required: true },
    selectedTemplateId: { type: [Number, String], default: null },
    isCustomQuery: { type: Boolean, required: true },
    isSuperAdmin: { type: Boolean, required: true },
  });

  const emit = defineEmits(["select-template", "select-custom", "edit-template", "delete-template"]);

  const { templatesSearchQuery } = storeToRefs(useSqlQueryStore());
</script>

<template>
  <ListContainer
    class="!static"
    element-name="query"
    :show-header="true">
    <template #header-action>
      <SearchInput
        v-model="templatesSearchQuery"
        placeholder="Search queries..."
        full-width
        class="flex-1" />
    </template>

    <template #elements>
      <ListItem
        v-if="isSuperAdmin"
        :index="-1"
        element-title="Custom Query"
        element-subtitle="Run any SQL query"
        :select-element="() => emit('select-custom')"
        :is-element-selected="() => isCustomQuery" />

      <ListItem
        v-for="(template, index) in templates"
        :key="template.id"
        :index="index"
        :element-title="template.name"
        :element-subtitle="template.description"
        :select-element="(idx) => emit('select-template', templates[idx])"
        :delete-element="isSuperAdmin ? (idx) => emit('delete-template', templates[idx]) : null"
        :confirm-deletion="true"
        :is-element-selected="(idx) => templates[idx]?.id === selectedTemplateId && !isCustomQuery">
        <template
          v-if="isSuperAdmin"
          #actions>
          <Button
            variant="ghost"
            size="icon-sm"
            @click.stop="emit('edit-template', templates[index])">
            <Pencil />
          </Button>
        </template>
      </ListItem>

      <EmptyState
        v-if="!templates.length && !isSuperAdmin"
        class="px-3 py-6"
        title="No queries available" />
    </template>
  </ListContainer>
</template>
